import { useEffect, useRef, useState } from "react";
import { useSearch, useLocation, useRoute } from "wouter";
import { useAuth } from "../../lib/auth";
import { api, MAX_MESSAGE_LENGTH } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import type { NegotiableContract } from "../../components/ProjectAgreement";
import type { InboxFolder } from "../../components/MessagesInbox";
import { useFocusedJobOffer } from "./useFocusedJobOffer";
import { useMessageInquiry } from "./useMessageInquiry";
import type { Compose } from "./types";
import { useConversationActions } from "./useConversationActions";
import { useFocusedActivitySource } from "./useFocusedActivitySource";
import { useConversationPolling } from "./useConversationPolling";
import { profileKey } from "../../lib/profileSession";
import { refreshFamily, requireRefreshArray, useRefreshOwner } from "../../lib/useRefreshOwner";
export function useMessages() {
  const { t: translate } = useI18n();
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const [, routeParams] = useRoute("/messages/:threadId");
  const routeThreadId = routeParams?.threadId || null;
  const requestedActivitySource = new URLSearchParams(search).get("activitySource");
  const requestedContractId = requestedActivitySource ? null : new URLSearchParams(search).get("negotiate");
  const requestedAgreementId = requestedActivitySource ? null : new URLSearchParams(search).get("agreement");
  const requestedJobOfferId = requestedActivitySource ? null : new URLSearchParams(search).get("jobOffer");

  const [threads, setThreads] = useState<Awaited<ReturnType<typeof api.listThreads>>["threads"]>([]);
  const [activeId, setActiveId] = useState<string | null>(routeThreadId);
  const [convo, setConvo] = useState<Awaited<ReturnType<typeof api.getThread>> | null>(null);
  const [compose, setCompose] = useState<Compose | null>(null);
  const [pendingContext, setPendingContext] = useState<Compose | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"active" | "archived">(() => new URLSearchParams(search).get("folder") === "archived" ? "archived" : "active");
  const [archivedCount, setArchivedCount] = useState(0);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [disputeUnreadCount, setDisputeUnreadCount] = useState(0);
  const [showActions, setShowActions] = useState(false);
  const [expandedTimeId, setExpandedTimeId] = useState<string | null>(null);
  const inboxRequestRef = useRef(0);
  const conversationRequestRef = useRef(0);
  // [messaging.async-recovery](../../../../docs/product/messaging-and-notifications.md#messaging-async-recovery): selection guards protect accounts; read ordering also prevents an
  // old poll from replacing a successful counterproposal in the same chat.
  const conversationReadRef = useRef(0);
  const [revisableProjectId, setRevisableProjectId] = useState<string | null>(null);
  const actions = useConversationActions({ user, activeId, convo, conversationRequestRef, conversationReadRef, setConvo, setErr, refreshConversation, navigate });
  const {
    quoteTarget, setQuoteTarget, arrangementTarget, setArrangementTarget, quoteBusy,
    setQuoteBusy, acceptanceToast, setAcceptanceToast, purchaseTarget, setPurchaseTarget,
    purchaseBusy, setPurchaseBusy, purchaseError, setPurchaseError, purchasePending,
    openServiceQuote, previewQuote, sendQuote, updateQuote, acceptArrangementRequest, updateItemQuote,
    sendArrangementQuote, decideArrangement, reviewArrangementPurchase, confirmArrangementPurchase,
  } = actions;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const agreementMessageRef = useRef<HTMLDivElement>(null);
  const [negotiationTarget, setNegotiationTarget] = useState<{ contract: NegotiableContract; request: number } | null>(null);
  const activitySource = useFocusedActivitySource({ sourceId: requestedActivitySource, sourceKind: new URLSearchParams(search).get("activitySourceKind"), returnToken: new URLSearchParams(search).get("activityReturn"), routeThreadId, convo });
  const focusedProposalMessageId = activitySource.requested ? activitySource.resolved?.messageId
    : [...(convo?.messages ?? [])].reverse().find(message => message.projectAgreement?.negotiationId === requestedAgreementId)?.id;

  const {focusedJobOffer,offerLoadError,setOfferRetry,focusedJobOfferMessageId,missingFocusedOffer} = useFocusedJobOffer({userId:user?.id,requestedJobOfferId,routeThreadId,convo});
  const offerMutationRequest = conversationRequestRef.current;

  // [contracts.workspace-navigation](../../../../docs/product/contracts.md#contracts-workspace-navigation): Contracts opens a private chat draft. A sent proposal is reused;
  // only explicit Send in the editor creates a new version in this conversation.
  useEffect(() => {
    setNegotiationTarget(null);
    if (!user || !routeThreadId || convo?.thread.id !== routeThreadId || !requestedContractId) return;
    let active = true;
    void api.listContracts().then(response => {
      if (!active) return;
      const contract: (NegotiableContract & { threadId?: string; sourceType: string; status: string; offerStatus: string; negotiationId?: string }) | undefined = response.contracts.find(candidate => candidate.id === requestedContractId && candidate.threadId === routeThreadId);
      if (!contract || contract.sourceType !== "project" || contract.status !== "pending_funding" || contract.offerStatus === "declined") {
        setErr("This project contract is unavailable for negotiation."); return;
      }
      if (contract.negotiationId) {
        navigate(`/messages/${encodeURIComponent(routeThreadId)}?agreement=${encodeURIComponent(contract.negotiationId)}`); return;
      }
      setNegotiationTarget({ contract, request: conversationRequestRef.current });
    }).catch(failure => { if (active) setErr(failure instanceof Error ? failure.message : "Could not open the contract proposal."); });
    return () => { active = false; };
  }, [user?.id, routeThreadId, requestedContractId, convo?.thread.id, navigate]);

  useEffect(() => {
    setBusy(false); setQuoteBusy(false); setActionBusy(false);
    setThreads([]); setConvo(null); setDraft(""); setPendingContext(null); setErr(null);
    // Account changes and unmounts invalidate both reads and mutation completions.
    ++inboxRequestRef.current; ++conversationRequestRef.current;
    return () => { ++inboxRequestRef.current; ++conversationRequestRef.current; };
  }, [user?.id]);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const inboxIdentity = `${profileKey(user)}:inbox:${tab}`;
  const inboxRefresh = useRefreshOwner(inboxIdentity, signal => [
    refreshFamily(async () => {
      const response = await api.listThreads(tab, signal);
      requireRefreshArray(response?.threads);
      if (!Number.isFinite(response.archivedCount)) throw new Error("Missing inbox counts.");
      return response;
    }, response => {
      setThreads(response.threads); setArchivedCount(response.archivedCount);
      if (tab === "active") setUnreadCount(response.threads.filter(thread => thread.unread).length);
    }, () => { setThreads([]); setArchivedCount(0); setUnreadCount(0); }),
    refreshFamily(() => api.myDisputes(signal), disputes => setDisputeUnreadCount(disputes.filter(dispute => dispute.unread).length), () => setDisputeUnreadCount(0)),
  ], { enabled: !!user, auto: false, primaryFamily: { index: 0, loadError: "Could not load chats. Please try again." } });
  const inboxLoading = !inboxRefresh.loaded && threads.length === 0;
  const inboxError = inboxRefresh.error;
  async function loadInbox(_which: "active" | "archived" = tab, background = false) {
    await inboxRefresh.reload(background);
  }
  useEffect(() => {
    if (!user) return;
    void loadInbox(tab);
    const refresh = () => { if (document.visibilityState === "visible") void loadInbox(tab, true); };
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { ++inboxRequestRef.current; clearInterval(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [user?.id, tab]);

  useEffect(() => {
    if (!user) return;
    const request = ++conversationRequestRef.current;
    setBusy(false); setQuoteBusy(false); setActionBusy(false); setAcceptanceToast(null);
    if (!routeThreadId) {
      setActiveId(null);
      setConvo(null);
      setConfirmDel(false);
      return;
    }
    setActiveId(routeThreadId);
    setCompose(null);
    setConfirmDel(false);
    setQuoteTarget(null); setArrangementTarget(null);
    setConvo(null); setErr(null); setShowActions(false);
    setExpandedTimeId(null);
    const read = ++conversationReadRef.current;
    api.getThread(routeThreadId).then((conversation) => {
      if (request === conversationRequestRef.current && read === conversationReadRef.current) { setConvo(conversation); void loadInbox(tab); }
    }).catch(() => { if (request === conversationRequestRef.current && read === conversationReadRef.current) setErr("Could not open conversation."); });
  }, [routeThreadId, user?.id]);

  useConversationPolling({ userId: user?.id, threadId: routeThreadId, exactSource: !!requestedActivitySource,
    requestRef: conversationRequestRef, readRef: conversationReadRef, setConversation: setConvo, setError: setErr });

  const activeThread = threads.find((t) => t.id === activeId) || convo?.thread;
  const isArchived = tab === "archived" || !!activeThread?.archived;
  // [messaging.contact-and-context](../../../../docs/product/messaging-and-notifications.md#messaging-contact-and-context): history permission covers existing contexts. A fresh
  // listing inquiry keeps its composer and is admitted separately by POST /threads.
  const canComposeInThread = convo?.thread.canMessage !== false
    || !!(pendingContext?.contextId && pendingContext.contextType !== "direct");

  function selectFolder(folder: InboxFolder) {
    if (folder === "disputes") { navigate("/messages/disputes"); return; }
    inboxRefresh.cancel();
    ++conversationRequestRef.current; ++inboxRequestRef.current;
    setBusy(false); setQuoteBusy(false); setActionBusy(false); setAcceptanceToast(null);
    setTab(folder); setActiveId(null); setConvo(null); setCompose(null); setPendingContext(null);
    setConfirmDel(false); setShowActions(false); setQuoteTarget(null); setArrangementTarget(null); setErr(null);
    navigate("/messages");
  }

  useEffect(() => {
    const projectId = [...(convo?.messages ?? [])].reverse().find((message) => message.contextType === "project")?.contextId ?? null;
    setRevisableProjectId(null);
    if (!projectId) return;
    let active = true;
    api.getBids(projectId).then((bids) => {
      if (active && !bids.isOwner && bids.myBid && bids.canEditBid) setRevisableProjectId(projectId);
    }).catch(() => {});
    return () => { active = false; };
  }, [convo]);

  async function archiveActive() {
    if (!activeId || actionBusy) return;
    const request = conversationRequestRef.current;
    setActionBusy(true); setErr(null);
    try {
      isArchived ? await api.unarchiveThread(activeId) : await api.archiveThread(activeId);
      if (request !== conversationRequestRef.current) return;
      selectFolder(tab); await loadInbox(tab);
    } catch (error: unknown) { if (request === conversationRequestRef.current) setErr(error instanceof Error ? error.message : "Could not update conversation."); }
    finally { if (request === conversationRequestRef.current) setActionBusy(false); }
  }
  async function deleteActive() {
    if (!activeId || actionBusy) return;
    const request = conversationRequestRef.current;
    setActionBusy(true); setErr(null);
    try {
      await api.deleteThread(activeId);
      if (request !== conversationRequestRef.current) return;
      selectFolder(tab); await loadInbox(tab);
    } catch (error: unknown) { if (request === conversationRequestRef.current) setErr(error instanceof Error ? error.message : "Could not delete conversation."); }
    finally { if (request === conversationRequestRef.current) setActionBusy(false); }
  }

  useMessageInquiry({ search, user, openThread, conversationRequestRef, setCompose, setDraft, setActiveId, setConvo });

  async function openThread(id: string, starter = "", context: Compose | null = null) {
    const request = ++conversationRequestRef.current;
    setBusy(false); setQuoteBusy(false); setActionBusy(false); setAcceptanceToast(null);
    setActiveId(id); setCompose(null); setConvo(null); setConfirmDel(false);
    setQuoteTarget(null); setArrangementTarget(null); setPendingContext(context);
    setDraft(starter); setShowActions(false); setErr(null);
    if (routeThreadId !== id) { navigate(`/messages/${id}`); return; }
    const read = ++conversationReadRef.current;
    try {
      const conversation = await api.getThread(id);
      if (request !== conversationRequestRef.current || read !== conversationReadRef.current) return;
      setConvo(conversation); await loadInbox(tab);
    } catch { if (request === conversationRequestRef.current && read === conversationReadRef.current) setErr("Could not open conversation."); }
  }

  useEffect(() => {
    if (!convo) return;
    const frame = requestAnimationFrame(() => {
      if (focusedProposalMessageId || focusedJobOfferMessageId || missingFocusedOffer) agreementMessageRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
      else messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    });
    return () => cancelAnimationFrame(frame);
  }, [convo?.thread?.id, convo?.messages?.length, requestedAgreementId, focusedProposalMessageId, focusedJobOfferMessageId, missingFocusedOffer]);

  // [messaging.async-recovery](../../../../docs/product/messaging-and-notifications.md#messaging-async-recovery): a server mutation still finishes for its original recipient, but
  // its refresh may only replace the selection/account that started it.
  async function refreshConversation(id: string, request: number) {
    if (request !== conversationRequestRef.current) return;
    const read = ++conversationReadRef.current;
    try {
      const conversation = await api.getThread(id);
      if (request !== conversationRequestRef.current || read !== conversationReadRef.current) return;
      setConvo(conversation); await loadInbox();
    } catch (failure) {
      if (request === conversationRequestRef.current && read === conversationReadRef.current) throw failure;
    }
  }

  async function sendInThread(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim(); if (!body || !activeId || busy || body.length > MAX_MESSAGE_LENGTH) return;
    const request = conversationRequestRef.current;
    setBusy(true); setErr(null);
    try {
      // Reusing a chat must still attach this listing or quote request to the
      // new message; a plain reply would discard that request's context.
      if (pendingContext) {
        const sent = await api.startThread({ toUserId: pendingContext.toUserId, contextType: pendingContext.contextType,
          contextId: pendingContext.contextId, subject: pendingContext.subject, body,
          requestQuote: pendingContext.requestQuote, bidId: pendingContext.bidId });
        if (request !== conversationRequestRef.current) return;
        if (sent.threadId !== activeId) {
          setDraft(""); navigate(`/messages/${sent.threadId}`); await openThread(sent.threadId); return;
        }
      } else await api.postMessage(activeId, body);
      if (request !== conversationRequestRef.current) return;
      setPendingContext(null);
      setDraft((current) => current.trim() === body ? "" : current);
      await refreshConversation(activeId, request);
    } catch (error: unknown) { if (request === conversationRequestRef.current) setErr(error instanceof Error ? error.message : "Could not send."); }
    finally { if (request === conversationRequestRef.current) setBusy(false); }
  }

  async function sendCompose(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim(); if (!body || !compose || busy || body.length > MAX_MESSAGE_LENGTH) return;
    const request = conversationRequestRef.current;
    setBusy(true); setErr(null);
    try {
      const r = await api.startThread({ toUserId: compose.toUserId, contextType: compose.contextType, contextId: compose.contextId, subject: compose.subject, body, requestQuote: compose.requestQuote, bidId: compose.bidId });
      if (request !== conversationRequestRef.current) return;
      setDraft((current) => current.trim() === body ? "" : current);
      await loadInbox();
      if (request !== conversationRequestRef.current) return;
      if (r?.threadId) { setCompose(null); navigate(`/messages/${r.threadId}`); openThread(r.threadId); }
    } catch (error: unknown) { if (request === conversationRequestRef.current) setErr(error instanceof Error ? error.message : "Could not send message."); }
    finally { if (request === conversationRequestRef.current) setBusy(false); }
  }


  const hasOpenConversation = activeId !== null || compose !== null;

  return {
    acceptArrangementRequest, acceptanceToast, actionBusy, activeId, agreementMessageRef,
    archiveActive, archivedCount, arrangementTarget, busy, canComposeInThread,
    compose, confirmArrangementPurchase, confirmDel, conversationRequestRef, convo,
    decideArrangement, deleteActive, disputeUnreadCount, draft, err,
    expandedTimeId, focusedJobOffer, focusedJobOfferMessageId, focusedProposalMessageId, hasOpenConversation,
    inboxError, inboxLoading, inboxRefresh, inboxIdentity, isArchived, loadInbox, loading,
    messagesEndRef, missingFocusedOffer, navigate, negotiationTarget, offerLoadError,
    offerMutationRequest, openServiceQuote, openThread, purchaseBusy, purchaseError,
    purchasePending, purchaseTarget, quoteBusy, quoteTarget, refreshConversation,
    reviewArrangementPurchase, revisableProjectId, selectFolder, sendArrangementQuote, sendCompose,
    sendInThread, previewQuote, sendQuote, setAcceptanceToast, setArrangementTarget, setConfirmDel,
    setDraft, setExpandedTimeId, setNegotiationTarget, setOfferRetry, setPurchaseError,
    setPurchaseTarget, setQuoteTarget, setShowActions, showActions, tab,
    threads, translate, unreadCount, updateItemQuote, updateQuote,
    user, activitySource, setErr,
  };
}
export type MessagesModel = ReturnType<typeof useMessages>;
